import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type DeliveryConfig, type Severity } from './api.ts';
import './delivery.css';

/**
 * Where this deployment's alerts are sent.
 *
 * An alert is raised when a control regresses between two assessments of the
 * same client. The console is where they are read; this is where they are
 * pushed to something an engineer already watches — a PSA, a ticket queue, an
 * automation platform. Delivery is deployment-level, like branding: one MSP
 * behind one deployment, one endpoint, and the client is named inside each
 * message rather than by which endpoint it arrived at.
 *
 * This is a panel, not a page: it renders inside Settings, which owns the h1.
 *
 * **This screen validates nothing.** What counts as an acceptable endpoint —
 * the scheme rule, the loopback exception, the refusal of credentials in a URL
 * — has exactly one implementation, in `packages/api/src/delivery.ts`. A second
 * copy here would be a rule that quietly stops matching the server it is
 * talking to, and this project has shipped that twice. The console posts what
 * it has and renders what the server says, including the severity list and the
 * payload version, which are served rather than restated.
 *
 * **The signing secret is write-only and that is the feature.** It is sealed
 * under the deployment's master key on the way in and the API has no route that
 * can return it. So this screen can say whether one is set and can replace it;
 * it cannot show it, and neither can anyone who reaches this console. The copy
 * says so plainly rather than leaving an operator to discover it by looking for
 * a reveal button that does not exist.
 */

const SEVERITY_TEXT: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const FIELD_LABELS: Record<string, string> = {
  enabled: 'Delivery',
  endpointUrl: 'Endpoint URL',
  minimumSeverity: 'Minimum severity',
  secret: 'Signing secret',
};

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

/**
 * Renders the server's own rejection rather than pre-empting it, the same way
 * `Branding.tsx` does. A 400 carries a Zod `flatten()`, whose field names are
 * the wire names; only the labels are ours, so the sentence reads as English
 * without the console deciding what was wrong with the request.
 */
const serverProblems = (cause: unknown): string[] | null => {
  if (!(cause instanceof ApiError) || cause.status !== 400) return null;

  const body = cause.body;
  if (typeof body !== 'object' || body === null) return null;

  const detail = (body as { error?: unknown }).error;
  if (typeof detail !== 'object' || detail === null) return null;

  const { formErrors, fieldErrors } = detail as { formErrors?: unknown; fieldErrors?: unknown };
  const problems: string[] = [];

  if (Array.isArray(formErrors)) problems.push(...formErrors.map(String));
  if (typeof fieldErrors === 'object' && fieldErrors !== null) {
    for (const [field, reasons] of Object.entries(fieldErrors as Record<string, unknown>)) {
      if (!Array.isArray(reasons)) continue;
      problems.push(...reasons.map((reason) => `${FIELD_LABELS[field] ?? field}: ${String(reason)}`));
    }
  }

  return problems.length > 0 ? problems : null;
};

/**
 * What the operator intends to do with a value they cannot see.
 *
 * Three states rather than a text box, because a text box over a write-only
 * field is ambiguous in the one way that matters: an empty box means both "I am
 * not touching this" and "remove it", and guessing wrong either silently keeps
 * a secret the operator meant to revoke or silently destroys a working one.
 */
type SecretIntent = 'keep' | 'replace' | 'clear';

/** The three-way `secret` the API expects, from the intent and the typed value. */
const secretToSend = (intent: SecretIntent, typed: string): string | null => {
  if (intent === 'keep') return null;
  if (intent === 'clear') return '';
  return typed;
};

/**
 * The integration reference, as one block of text.
 *
 * An integrator building the receiving half will read this screen and nothing
 * else, so it is written to be copied whole into whatever they are writing in.
 * The payload version is interpolated from what the server reported rather than
 * typed here, so a bump in `packages/contracts` cannot leave this paragraph
 * describing a version nothing sends.
 */
const receiverReference = (payloadVersion: string): string =>
  `POST https://your-endpoint.example/hooks/driftwatch
Content-Type: application/json
X-DriftWatch-Event: alert.raised
X-DriftWatch-Delivery: 8f14e45f-ceea-467a-9c1e-2b6b0c3d51aa
X-DriftWatch-Payload-Version: ${payloadVersion}
X-DriftWatch-Signature: t=1772550764,v1=4c9a…

  t   unix seconds, when the request was signed
  v1  HMAC-SHA256(secret, t + "." + body), lower-case hex
      "body" is the exact bytes of the request, before any parsing

{
  "version": "${payloadVersion}",
  "event": "alert.raised",
  "alertId": "8f14e45f-ceea-467a-9c1e-2b6b0c3d51aa",
  "client": { "id": "…", "name": "Aurora Dental" },
  "ticket": {
    "title": "Aurora Dental — “Guest access is restricted” is no longer met",
    "body": "Several plain-text sentences, safe to paste into a ticket.",
    "severity": "high",
    "controlId": "AC-007"
  },
  "observedAt": "2026-03-04T09:12:44.000Z",
  "consoleUrl": "https://your-console.example/?alert=8f14e45f…"
}`;

export const Delivery = () => {
  const [draft, setDraft] = useState<DeliveryConfig | null>(null);
  const [severities, setSeverities] = useState<Severity[]>([]);
  const [payloadVersion, setPayloadVersion] = useState('1');
  const [intent, setIntent] = useState<SecretIntent>('keep');
  const [typedSecret, setTypedSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  /**
   * A screen reader does not re-announce a live region whose text is unchanged,
   * and saving twice in a row is ordinary here. The alternating zero-width
   * space guarantees a change without a visible one.
   */
  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous.endsWith('\u200B') ? text : `${text}\u200B`));
  }, []);

  useEffect(() => {
    api
      .getDelivery()
      .then((view) => {
        setDraft(view.delivery);
        setSeverities(view.severities);
        setPayloadVersion(view.payloadVersion);
      })
      .catch((cause: unknown) => setError(message(cause)))
      .finally(() => setLoading(false));
  }, []);

  const patch = (change: Partial<DeliveryConfig>) =>
    setDraft((previous) => (previous === null ? previous : { ...previous, ...change }));

  const save = async () => {
    if (draft === null) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const saved = await api.putDelivery({
        enabled: draft.enabled,
        endpointUrl: draft.endpointUrl,
        minimumSeverity: draft.minimumSeverity,
        secret: secretToSend(intent, typedSecret),
      });
      setDraft(saved.delivery);
      setSeverities(saved.severities);
      setPayloadVersion(saved.payloadVersion);
      setIntent('keep');
      setTypedSecret('');

      const said = saved.delivery.enabled
        ? 'Delivery settings saved. Alerts raised from now on will be posted to your endpoint.'
        : 'Delivery settings saved. Delivery is switched off, so alerts are recorded in the console only.';
      setStatus(said);
      announce(said);
    } catch (cause) {
      const problems = serverProblems(cause);
      setError(problems ? problems.join(' ') : message(cause));
      announce('Those settings were not saved. Nothing has changed.');
    } finally {
      setBusy(false);
    }
  };

  const copyReference = async () => {
    try {
      await navigator.clipboard.writeText(receiverReference(payloadVersion));
      announce('The integration reference has been copied to the clipboard.');
      setStatus('Integration reference copied.');
    } catch {
      // Clipboard access is refused in plenty of ordinary configurations. The
      // text is on screen and selectable, so this costs a manual selection.
      announce('The clipboard is unavailable. Select the reference text and copy it.');
      setStatus('The clipboard is unavailable — select the text below and copy it.');
    }
  };

  return (
    <section className="dl-panel" aria-labelledby="delivery-heading">
      <h2 id="delivery-heading">Alert delivery</h2>
      <p className="lead">
        When a control moves backwards for one of your clients, DriftWatch raises an alert. This is
        where those alerts are posted, so they land in the queue your engineers already watch rather
        than waiting for somebody to open this console.
      </p>

      <p aria-live="polite" role="status" className="sr-only">
        {announcement}
      </p>

      <div className="callout dl-scope">
        <span className="callout-title">One endpoint for every client you manage</span>
        Delivery is configured for the whole deployment, not per client. Each message names the
        client it concerns, so a receiver can route on that field rather than on which address it
        arrived at.
      </div>

      {error && <div className="error">{error}</div>}

      {loading || draft === null ? (
        <p className="note">Loading…</p>
      ) : (
        <div className="dl-grid">
          <div className="card dl-form">
            <div className="card-head">
              <h3>Where alerts are sent</h3>
            </div>
            <div className="card-body">
              <div className="field">
                <label htmlFor="delivery-enabled">Delivery</label>
                <span className="dl-check">
                  <input
                    id="delivery-enabled"
                    type="checkbox"
                    checked={draft.enabled}
                    onChange={(event) => patch({ enabled: event.target.checked })}
                  />
                  <label htmlFor="delivery-enabled">Post alerts to the endpoint below</label>
                </span>
                <span className="hint">
                  With this off, alerts are still raised and still appear on the Alerts screen.
                  Nothing is sent anywhere.
                </span>
              </div>

              <div className="field">
                <label htmlFor="delivery-endpoint">Endpoint URL</label>
                {/*
                  Deliberately not type="url": the browser's own validation
                  would refuse the request before it was made, and the operator
                  would get a generic tooltip instead of the server's sentence
                  explaining which rule they hit and why it exists.
                */}
                <input
                  id="delivery-endpoint"
                  type="text"
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  value={draft.endpointUrl ?? ''}
                  placeholder="https://alerts.example.com/hooks/driftwatch"
                  aria-describedby="delivery-endpoint-help"
                  onChange={(event) => patch({ endpointUrl: event.target.value || null })}
                />
                <span className="hint" id="delivery-endpoint-help">
                  Must be https. Every message names one of your clients and a control of theirs
                  that has just stopped being met, which is not something to put on the wire in
                  clear. A listener on <span className="mono">localhost</span> may use http, so you
                  can build and test the receiving half before you deploy it.
                </span>
              </div>

              <div className="field">
                <label htmlFor="delivery-severity">Minimum severity</label>
                <select
                  id="delivery-severity"
                  value={draft.minimumSeverity}
                  aria-describedby="delivery-severity-help"
                  onChange={(event) =>
                    patch({ minimumSeverity: event.target.value as Severity })
                  }
                >
                  {severities.map((severity) => (
                    <option key={severity} value={severity}>
                      {SEVERITY_TEXT[severity] ?? severity}
                    </option>
                  ))}
                </select>
                <span className="hint" id="delivery-severity-help">
                  Alerts below this are recorded in the console and not delivered. A floor set too
                  low costs more than it saves: an alert channel that interrupts people for
                  everything is one nobody reads by month three.
                </span>
              </div>

              <div className="field dl-secret">
                <label htmlFor="delivery-secret">Signing secret</label>

                <p className={`status-inline dl-secret-state${draft.hasSecret ? ' dl-secret-set' : ''}`}>
                  {draft.hasSecret ? 'A signing secret is set.' : 'No signing secret set.'}
                  {intent === 'clear' && ' It will be removed when you save.'}
                  {intent === 'replace' && ' It will be replaced when you save.'}
                </p>

                {intent === 'replace' ? (
                  <>
                    <input
                      id="delivery-secret"
                      type="password"
                      autoComplete="new-password"
                      spellCheck={false}
                      value={typedSecret}
                      placeholder="Paste the value your receiver will verify with"
                      aria-describedby="delivery-secret-help"
                      onChange={(event) => setTypedSecret(event.target.value)}
                    />
                    <div className="toolbar dl-secret-actions">
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        onClick={() => {
                          setIntent('keep');
                          setTypedSecret('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="toolbar dl-secret-actions">
                    <button
                      type="button"
                      className="btn btn-quiet btn-sm"
                      onClick={() => setIntent('replace')}
                    >
                      {draft.hasSecret ? 'Replace secret' : 'Set a signing secret'}
                    </button>
                    {draft.hasSecret && intent !== 'clear' && (
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        onClick={() => setIntent('clear')}
                      >
                        Remove secret
                      </button>
                    )}
                    {intent === 'clear' && (
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        onClick={() => setIntent('keep')}
                      >
                        Keep the stored secret
                      </button>
                    )}
                  </div>
                )}

                <span className="hint" id="delivery-secret-help">
                  The secret is encrypted before it is stored and there is no way to read it back —
                  not through this console, not through the API, not by anyone who reaches either.
                  That is the point of it: the only copies are yours and your receiver&rsquo;s. If
                  you lose it, generate a new one here and at the far end; it cannot be recovered,
                  only replaced.
                </span>
              </div>

              <div className="callout callout-evidence dl-visibility">
                <span className="callout-title">
                  Visibility alerts are delivered whatever the floor is set to
                </span>
                When DriftWatch can no longer assess a control for a client — a licence lapse, a
                narrowed consent — that is not a finding about their security, and filtering it by
                the control&rsquo;s severity compares two unrelated things. A medium-severity
                control we have stopped being able to see is a gap in your monitoring, so it is sent
                even when the floor is set above it.
              </div>

              <div className="toolbar dl-actions">
                <button
                  className="btn btn-primary"
                  aria-busy={busy || undefined}
                  disabled={busy}
                  onClick={() => void save()}
                >
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>

              {status && <p className="status-inline dl-status">{status}</p>}
              {draft.updatedAt && (
                <p className="note dl-updated">
                  Last changed {new Date(draft.updatedAt).toLocaleString('en-GB')}
                </p>
              )}
            </div>
          </div>

          <div className="dl-side">
            <h3>What your receiver gets</h3>
            <p className="note dl-side-note">
              One JSON POST per alert. Everything below is what arrives; there is no other shape and
              no second call.
            </p>

            <pre className="dl-ref">{receiverReference(payloadVersion)}</pre>

            <div className="toolbar dl-ref-actions">
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => void copyReference()}>
                Copy this reference
              </button>
            </div>

            <div className="callout dl-verify">
              <span className="callout-title">Verifying a delivery</span>
              <ul className="dl-list">
                <li>
                  Compute <span className="mono">HMAC-SHA256</span> over{' '}
                  <span className="mono">t + &quot;.&quot; + body</span> with your signing secret,
                  and compare it with <span className="mono">v1</span> using a constant-time
                  comparison.
                </li>
                <li>
                  Sign the <em>raw</em> body. Re-serialising the parsed JSON reorders keys and
                  changes whitespace, and the signature will not match.
                </li>
                <li>
                  Reject a request whose <span className="mono">t</span> is far from your own clock
                  — a few minutes is usual — so a captured delivery cannot be replayed later.
                </li>
                <li>
                  <span className="mono">X-DriftWatch-Delivery</span> is the alert id and is stable
                  across retries. Use it as the external reference on whatever you open, so a retry
                  updates that ticket rather than raising a second one.
                </li>
              </ul>
            </div>

            <div className="callout callout-caution dl-withheld">
              <span className="callout-title">No reports and no evidence are sent</span>
              The payload carries the control, how its verdict moved, the client&rsquo;s name and a
              link back into this console. It does not carry evidence — no user principal names, no
              device identifiers, no policy names, no report. Evidence is customer configuration and
              it stays behind this deployment&rsquo;s own authentication, which a webhook to a
              third-party system is not.
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
